import { useRef, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Dialog,
  Field,
  Flex,
  HStack,
  Input,
  Portal,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react'
import { LuPencil, LuPlus, LuTag, LuTrash2 } from 'react-icons/lu'
import { useParams } from 'react-router'
import { AxiosError } from 'axios'
import { showSuccess } from '@/lib/alert'
import type { ApiErrorResponse } from '@/lib/api'
import CustomDialogBackdrop from '@/components/CustomDialogBackdrop'
import ConfirmActionDialog from '@/components/ConfirmActionDialog'
import { useExpenseCategories } from '@/hooks/agendas/useExpenseCategories'
import { useCreateExpenseCategory } from '@/hooks/agendas/useCreateExpenseCategory'
import { useUpdateExpenseCategory } from '@/hooks/agendas/useUpdateExpenseCategory'
import { useDeleteExpenseCategory } from '@/hooks/agendas/useDeleteExpenseCategory'
import type { ExpenseCategory } from '@/services/expenseCategories'

type CategoryFormDialogProps = {
  agendaId: string
  category?: ExpenseCategory
  onClose: () => void
}

const CategoryFormDialog = ({ agendaId, category, onClose }: CategoryFormDialogProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const isEditing = !!category

  const createMutation = useCreateExpenseCategory(agendaId)
  const updateMutation = useUpdateExpenseCategory(agendaId)

  const mutation = isEditing ? updateMutation : createMutation

  const validationErrors = (mutation.error as AxiosError<ApiErrorResponse> | null)?.response?.data
    ?.validation_errors

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) mutation.reset()
    if (!nextOpen) onClose()
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const name = (new FormData(e.currentTarget).get('name') as string).trim()

    if (isEditing) {
      updateMutation.mutate(
        { categoryId: category.id, payload: { name } },
        {
          onSuccess: () => {
            showSuccess('Categoria atualizada com sucesso!')
            onClose()
          },
        },
      )
    } else {
      createMutation.mutate(
        { name },
        {
          onSuccess: () => {
            showSuccess('Categoria criada com sucesso!')
            onClose()
          },
        },
      )
    }
  }

  return (
    <Dialog.Root
      size="sm"
      placement="center"
      open
      onOpenChange={({ open }) => handleOpenChange(open)}
      initialFocusEl={() => inputRef.current}
    >
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content m={6}>
            <form onSubmit={handleSubmit}>
              <Dialog.Header>
                <Dialog.Title>{isEditing ? 'Editar categoria' : 'Nova categoria'}</Dialog.Title>
              </Dialog.Header>

              <Dialog.Body>
                <Stack gap={4}>
                  <Field.Root required invalid={!!validationErrors?.name}>
                    <Field.Label>
                      Nome
                      <Field.RequiredIndicator />
                    </Field.Label>
                    <Input
                      ref={inputRef}
                      name="name"
                      placeholder="Ex: Impostos"
                      variant="subtle"
                      defaultValue={category?.name ?? ''}
                    />
                    {validationErrors?.name?.map((error, index) => (
                      <Field.ErrorText key={index}>{error}</Field.ErrorText>
                    ))}
                  </Field.Root>
                </Stack>
              </Dialog.Body>

              <Dialog.Footer>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={mutation.isPending}
                >
                  Cancelar
                </Button>
                <Button size="sm" type="submit" colorPalette="primary" loading={mutation.isPending}>
                  {isEditing ? 'Salvar' : 'Criar'}
                </Button>
              </Dialog.Footer>
            </form>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

const ExpenseCategoriesTab = () => {
  const { agendaId } = useParams<{ agendaId: string }>()
  const [formTarget, setFormTarget] = useState<ExpenseCategory | null | 'new'>(null)
  const [deleteTarget, setDeleteTarget] = useState<ExpenseCategory | null>(null)

  const { data: categories, isLoading } = useExpenseCategories(agendaId!)
  const deleteMutation = useDeleteExpenseCategory(agendaId!)

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        showSuccess('Categoria removida com sucesso!')
        setDeleteTarget(null)
      },
    })
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <Spinner size="lg" color="primary.500" />
      </Box>
    )
  }

  return (
    <Stack gap={4} px={4} py={4}>
      <Flex justify="flex-end">
        <Button size="sm" colorPalette="primary" onClick={() => setFormTarget('new')}>
          <LuPlus />
          Nova categoria
        </Button>
      </Flex>

      {!categories || categories.length === 0 ? (
        <Box display="flex" flexDir="column" alignItems="center" gap={2} py={10} color="fg.muted">
          <LuTag size={32} />
          <Text fontSize="sm">Nenhuma categoria encontrada.</Text>
        </Box>
      ) : (
        categories.map((category) => (
          <Card.Root key={category.id} variant="outline" w="full">
            <Card.Body>
              <Flex justify="space-between" align="center" gap={2}>
                <Text fontWeight="medium">{category.name}</Text>
                <HStack gap={2}>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => setFormTarget(category)}
                  >
                    <LuPencil />
                    Editar
                  </Button>
                  <Button
                    size="xs"
                    colorPalette="red"
                    variant="outline"
                    onClick={() => setDeleteTarget(category)}
                  >
                    <LuTrash2 />
                    Remover
                  </Button>
                </HStack>
              </Flex>
            </Card.Body>
          </Card.Root>
        ))
      )}

      {formTarget !== null && (
        <CategoryFormDialog
          agendaId={agendaId!}
          category={formTarget === 'new' ? undefined : formTarget}
          onClose={() => setFormTarget(null)}
        />
      )}

      {deleteTarget && (
        <ConfirmActionDialog
          open
          onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}
          title="Remover categoria"
          body={<>Tem certeza que deseja remover a categoria <strong>{deleteTarget.name}</strong>? Esta ação não poderá ser desfeita.</>}
          onConfirm={handleDeleteConfirm}
          confirmLabel="Remover"
          confirmColorPalette="red"
          isPending={deleteMutation.isPending}
        />
      )}
    </Stack>
  )
}

export default ExpenseCategoriesTab
